import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus,
  X,
  Calendar,
  Clock,
  Video,
  User,
  CalendarOff,
} from 'lucide-react'
import { useMeetings, useCreateMeeting } from '../api/meetings'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../components/ui/Table'
import { cn } from '../lib/utils'
import type { MeetingStatus } from '@1130-studio/shared'

// --- Status config ---

const statusConfig: Record<
  MeetingStatus,
  { label: string; variant: 'info' | 'warning' | 'success' | 'violet' | 'danger' }
> = {
  scheduled: { label: 'Запланирована', variant: 'info' },
  ongoing: { label: 'В процессе', variant: 'warning' },
  completed: { label: 'Завершена', variant: 'success' },
  transcribed: { label: 'Транскрибирована', variant: 'violet' },
  cancelled: { label: 'Отменена', variant: 'danger' },
}

function MeetingStatusBadge({ status }: { status: MeetingStatus }) {
  const config = statusConfig[status] || { label: status, variant: 'default' as const }
  return <Badge variant={config.variant}>{config.label}</Badge>
}

// --- Filter tabs ---

const statusFilters: { label: string; value: MeetingStatus | undefined }[] = [
  { label: 'Все', value: undefined },
  { label: 'Запланированные', value: 'scheduled' },
  { label: 'Завершённые', value: 'completed' },
  { label: 'Отменённые', value: 'cancelled' },
]

// --- Duration formatter ---

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} мин`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}ч ${m}мин` : `${h}ч`
}

// --- Loading skeleton ---

function TableSkeleton() {
  return (
    <div className="space-y-0">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-4 border-b border-white/10 animate-pulse"
        >
          <div className="h-4 w-40 bg-white/10 rounded" />
          <div className="h-4 w-28 bg-white/10 rounded" />
          <div className="h-4 w-24 bg-white/10 rounded" />
          <div className="h-4 w-32 bg-white/10 rounded" />
          <div className="h-4 w-16 bg-white/10 rounded" />
          <div className="h-6 w-24 bg-white/10 rounded-full" />
        </div>
      ))}
    </div>
  )
}

// --- Create meeting modal ---

interface CreateMeetingModalProps {
  leadId?: string
  onClose: () => void
  onSuccess: () => void
}

function CreateMeetingModal({ leadId, onClose, onSuccess }: CreateMeetingModalProps) {
  const createMeeting = useCreateMeeting()

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [durationMinutes, setDurationMinutes] = useState(60)
  const [meetingUrl, setMeetingUrl] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (!title.trim()) {
      setError('Укажите название встречи')
      return
    }
    if (!scheduledAt) {
      setError('Укажите дату и время')
      return
    }

    try {
      await createMeeting.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes,
        meetingUrl: meetingUrl.trim() || undefined,
        leadId,
      })
      onSuccess()
    } catch (err) {
      console.error('Failed to create meeting:', err)
      setError('Не удалось создать встречу')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <Card className="relative w-full max-w-lg mx-4 border-border-medium z-10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Назначить встречу</CardTitle>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Название *"
              placeholder="Знакомство с клиентом"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Описание
              </label>
              <textarea
                rows={3}
                className="w-full px-4 py-3 bg-bg-tertiary border border-border-medium rounded-md text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-lime focus:ring-1 focus:ring-accent-lime/20 transition-all duration-200 resize-none"
                placeholder="Дополнительная информация о встрече..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                type="datetime-local"
                label="Дата и время *"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
              <Input
                type="number"
                label="Длительность (мин)"
                placeholder="60"
                min={5}
                max={480}
                value={String(durationMinutes)}
                onChange={(e) => setDurationMinutes(Number(e.target.value) || 60)}
              />
            </div>

            <Input
              label="Ссылка на встречу"
              placeholder="https://meet.google.com/..."
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
            />

            {error && (
              <p className="text-sm text-red-400">{error}</p>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={onClose}>
                Отмена
              </Button>
              <Button type="submit" loading={createMeeting.isPending}>
                Создать
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

// --- Main page ---

export default function MeetingsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const leadId = searchParams.get('leadId') || undefined
  const [statusFilter, setStatusFilter] = useState<MeetingStatus | undefined>()
  const [page, setPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)

  const { data, isLoading, error } = useMeetings({
    status: statusFilter,
    page,
    pageSize: 20,
  })

  const meetings = data?.items || []
  const totalPages = data?.totalPages || 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Встречи</h1>
          <p className="text-text-secondary mt-1">
            Управление встречами и созвонами
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4" />
          Назначить встречу
        </Button>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {statusFilters.map((filter) => (
          <button
            key={filter.label}
            onClick={() => {
              setStatusFilter(filter.value)
              setPage(1)
            }}
            className={cn(
              'px-3 py-2 rounded-md text-sm font-medium transition-colors',
              statusFilter === filter.value
                ? 'bg-accent-lime-dim text-accent-lime'
                : 'bg-bg-tertiary text-text-secondary hover:text-text-primary'
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <TableSkeleton />
        ) : error ? (
          <div className="text-center py-12 text-red-400">
            Ошибка загрузки встреч
          </div>
        ) : meetings.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-text-tertiary">
            <CalendarOff className="w-12 h-12 mb-4 opacity-40" />
            <p className="text-lg font-medium mb-1">
              {statusFilter ? 'Встречи не найдены' : 'Пока нет встреч'}
            </p>
            <p className="text-sm">
              {statusFilter
                ? 'Попробуйте изменить фильтр'
                : 'Назначьте первую встречу, нажав кнопку выше'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Название</TableHead>
                <TableHead>Контакт</TableHead>
                <TableHead>Ответственный</TableHead>
                <TableHead>Дата и время</TableHead>
                <TableHead>Длительность</TableHead>
                <TableHead>Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {meetings.map((meeting) => (
                <TableRow
                  key={meeting.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/meetings/${meeting.id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-md bg-accent-lime/10 flex items-center justify-center">
                        <Video className="w-4 h-4 text-accent-lime" />
                      </div>
                      <div>
                        <div className="font-medium">{meeting.title}</div>
                        {meeting.description && (
                          <div className="text-sm text-text-tertiary truncate max-w-xs">
                            {meeting.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {meeting.lead ? (
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-text-tertiary" />
                        <span className="text-text-secondary">{meeting.lead.name}</span>
                      </div>
                    ) : (
                      <span className="text-text-tertiary">--</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {meeting.host ? (
                      <span className="text-text-secondary">{meeting.host.username}</span>
                    ) : (
                      <span className="text-text-tertiary">--</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-text-secondary">
                      <Calendar className="w-3.5 h-3.5 text-text-tertiary" />
                      <div>
                        <div>
                          {new Date(meeting.scheduledAt).toLocaleDateString('ru-RU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </div>
                        <div className="text-xs text-text-tertiary">
                          {new Date(meeting.scheduledAt).toLocaleTimeString('ru-RU', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-text-secondary">
                      <Clock className="w-3.5 h-3.5 text-text-tertiary" />
                      {formatDuration(meeting.durationMinutes)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <MeetingStatusBadge status={meeting.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border-subtle">
            <div className="text-sm text-text-tertiary">
              Страница {page} из {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Назад
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Далее
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create meeting modal */}
      {showCreateModal && (
        <CreateMeetingModal
          leadId={leadId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => setShowCreateModal(false)}
        />
      )}
    </div>
  )
}
